import { Box, Typography, Container, Paper, CircularProgress, Chip } from "@mui/material";
import { useEffect, useState } from "react";
import { baseURL, pyApiURL } from "../../utils/constant";
import TerminalIcon from "@mui/icons-material/Terminal";
import WebIcon from "@mui/icons-material/Web";
import StorageIcon from "@mui/icons-material/Storage";
import DataObjectIcon from "@mui/icons-material/DataObject";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

const UI_VERSION = process.env.REACT_APP_VERSION || "1.0.4";

function VersionCard({ label, icon, version, loading, error, color, bg }) {
  const isOk = !loading && !error && version;
  return (
    <Paper
      elevation={0}
      sx={{
        p: 0,
        borderRadius: 4,
        border: "1px solid #e2e8f0",
        overflow: "hidden",
        flex: "1 1 0",
        minWidth: 220,
        transition: "box-shadow 0.2s, transform 0.2s",
        "&:hover": { boxShadow: "0 8px 32px rgba(0,0,0,0.08)", transform: "translateY(-2px)" },
      }}
    >
      {/* Colored header strip */}
      <Box sx={{ background: `linear-gradient(135deg, ${color}, ${bg})`, px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: "10px", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {icon}
        </Box>
        <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#fff", letterSpacing: "0.02em" }}>
          {label}
        </Typography>
      </Box>
      {/* Version body */}
      <Box sx={{ px: 3, py: 3, textAlign: "center" }}>
        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            <CircularProgress size={28} sx={{ color }} />
            <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>Checking...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            <ErrorIcon sx={{ fontSize: 28, color: "#ef4444" }} />
            <Typography sx={{ color: "#ef4444", fontSize: "0.8rem", fontWeight: 600 }}>{error}</Typography>
          </Box>
        ) : (
          <>
            <Typography sx={{ fontSize: "2rem", fontWeight: 800, color: "#1e293b", lineHeight: 1.2, fontFamily: "'JetBrains Mono', monospace" }}>
              v{version}
            </Typography>
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
              label="Online"
              size="small"
              sx={{ mt: 1.5, fontWeight: 600, fontSize: "0.7rem", background: "#ecfdf5", color: "#059669", border: "1px solid #a7f3d0" }}
            />
          </>
        )}
      </Box>
    </Paper>
  );
}

export default function AppVersion() {
  const [javaVersion, setJavaVersion] = useState({ version: null, loading: true, error: null });
  const [pyVersion, setPyVersion] = useState({ version: null, loading: true, error: null });

  const fetchVersion = (url, setter) => {
    fetch(url, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Unavailable");
        return res.json();
      })
      .then((data) => setter({ version: data.version, loading: false, error: null }))
      .catch((err) => setter({ version: null, loading: false, error: err.message }));
  };

  useEffect(() => {
    fetchVersion(baseURL + "aboutBuildVersion", setJavaVersion);
    fetchVersion(pyApiURL + "v4/aboutBuildApiVersion", setPyVersion);
  }, []);

  return (
    <Container maxWidth="md" sx={{ mt: 6, mb: 6 }}>
      {/* Page Header */}
      <Box sx={{ textAlign: "center", mb: 5 }}>
        <Box sx={{ width: 56, height: 56, borderRadius: "16px", background: "linear-gradient(135deg, #3c5a98, #8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
          <TerminalIcon sx={{ fontSize: 28, color: "#fff" }} />
        </Box>
        <Typography sx={{ fontSize: "1.4rem", fontWeight: 800, color: "#1e293b" }}>
          Build Information
        </Typography>
        <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", mt: 0.5 }}>
          Current versions of all application services
        </Typography>
      </Box>

      {/* Version Cards */}
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", justifyContent: "center" }}>
        <VersionCard
          label="Frontend (UI)"
          icon={<WebIcon sx={{ fontSize: 20, color: "#fff" }} />}
          version={UI_VERSION}
          loading={false}
          error={null}
          color="#3c5a98"
          bg="#6380c0"
        />
        <VersionCard
          label="Java API"
          icon={<StorageIcon sx={{ fontSize: 20, color: "#fff" }} />}
          color="#10b981"
          bg="#34d399"
          {...javaVersion}
        />
        <VersionCard
          label="Python API"
          icon={<DataObjectIcon sx={{ fontSize: 20, color: "#fff" }} />}
          color="#8b5cf6"
          bg="#a78bfa"
          {...pyVersion}
        />
      </Box>
    </Container>
  );
}
